import { Component, DestroyRef, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PageEvent } from '@angular/material/paginator';
import { Chart, ChartConfiguration, Plugin } from 'chart.js';
import { finalize } from 'rxjs';
import { ThemeService } from 'src/app/core/services/theme.service';
import { IFormControlConfig } from 'src/app/features/reactive-forms-ui/interfaces/form-control-config';
import {
  SmilebackReview,
  SmilebackReviewsPage,
  SmilebackSummary,
  TicketAnalysisFilters,
} from '../../../models/ticket-analysis.models';
import { TicketAnalysisService } from '../../../services/ticket-analysis.service';
import { ViewPersistenceService } from 'src/app/features/lifecycle-command/services/view-persistence.service';
import {
  TICKET_ANALYSIS_FILTERS_LAYOUT,
  applyFilterDropdowns,
  createTicketAnalysisFilterConfigMap,
  createTicketAnalysisFiltersForm,
  defaultDateFrom,
  extractBaseFilters,
} from '../ticket-analysis-filters.utils';
import { BAR_CATEGORY_PERCENTAGE, BAR_PERCENTAGE, VARIANCE_YEAR_LABEL_COLOR } from '../ticket-analysis-chart.constants';

const SMILEBACK_POSITIVE_COLOR = '#00c100';
const SMILEBACK_NEUTRAL_COLOR = '#d8b706';
const SMILEBACK_NEGATIVE_COLOR = '#bf360c';

@Component({
  selector: 'ticket-analysis-smileback',
  templateUrl: './ticket-analysis-smileback.component.html',
  styleUrls: ['./ticket-analysis-smileback.component.scss'],
  standalone: false,
})
export class TicketAnalysisSmilebackComponent implements OnInit, OnDestroy {
  loading = false;
  loadingReviews = false;
  error = '';
  summary: SmilebackSummary | null = null;
  reviewsPage: SmilebackReviewsPage | null = null;

  currentPage = 1;
  pageSize = 25;
  readonly displayedColumns = ['ticketId', 'dateEntered', 'closedDate', 'rating', 'contactName', 'comment'];

  csatBarChartData: any = { labels: [], datasets: [] };
  csatBarOptions: any = {};
  gaugeChartData: ChartConfiguration<'doughnut'>['data'] = { datasets: [] };
  gaugeOptions: ChartConfiguration<'doughnut'>['options'] = {};

  private labelColor = '#555555';
  private yearGroups: { year: string; start: number; end: number }[] = [];

  readonly csatChartPlugins: Plugin[] = [
    {
      id: 'csatBarTopLabels',
      afterDatasetsDraw: (chart: Chart) => {
        const { ctx, data } = chart;
        const nMonths = data.labels?.length ?? 0;

        // Total count rotated above each bar
        for (let j = 0; j < nMonths; j++) {
          const total = [0, 1, 2].reduce((sum, di) => sum + ((data.datasets[di]?.data[j] as number) ?? 0), 0);
          if (!total) continue;
          let topEl: any = null;
          for (let di = 2; di >= 0; di--) {
            const val = data.datasets[di]?.data[j] as number;
            if (val > 0) {
              topEl = chart.getDatasetMeta(di).data[j];
              break;
            }
          }
          if (!topEl) continue;
          const { x, y } = topEl.getProps(['x', 'y'], true);
          ctx.save();
          ctx.translate(x, y - 10);
          ctx.rotate(-Math.PI / 2);
          ctx.font = '400 12px Inter, sans-serif';
          ctx.fillStyle = this.labelColor;
          ctx.textAlign = 'left';
          ctx.textBaseline = 'middle';
          ctx.fillText(String(total), 0, 0);
          ctx.restore();
        }

        // Count centered inside each bar segment
        for (let di = 0; di <= 2; di++) {
          const meta = chart.getDatasetMeta(di);
          if (!meta.visible) continue;
          for (let j = 0; j < nMonths; j++) {
            const val = (data.datasets[di]?.data[j] as number) ?? 0;
            if (!val) continue;
            const bar = meta.data[j] as any;
            const { x, y, base } = bar.getProps(['x', 'y', 'base'], true);
            const segHeight = Math.abs(base - y);
            if (segHeight < 14) continue;
            ctx.save();
            ctx.font = '600 11px Inter, sans-serif';
            ctx.fillStyle = '#ffffff';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(String(val), x, y + segHeight / 2);
            ctx.restore();
          }
        }
      },
    },
    {
      id: 'csatLinePctLabels',
      afterDatasetsDraw: (chart: Chart) => {
        const { ctx, data } = chart;
        const lineDatasetIndex = 3;
        const meta = chart.getDatasetMeta(lineDatasetIndex);
        const nMonths = data.labels?.length ?? 0;
        for (let j = 0; j < nMonths; j++) {
          const val = data.datasets[lineDatasetIndex]?.data[j] as number;
          if (val == null) continue;
          const point = meta.data[j] as any;
          const { x, y } = point.getProps(['x', 'y'], true);
          ctx.save();
          ctx.font = '600 11px Inter, sans-serif';
          ctx.fillStyle = this.labelColor;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'bottom';
          ctx.fillText(`${val}%`, x, y - 6);
          ctx.restore();
        }
      },
    },
    {
      id: 'csatYearGroupLabels',
      afterDraw: (chart: Chart) => {
        if (!this.yearGroups.length) return;
        const { ctx, scales, chartArea } = chart;
        const xAxis = scales['x'];
        if (!xAxis || !chartArea) return;
        ctx.save();
        ctx.font = 'italic 600 14px Inter, sans-serif';
        ctx.fillStyle = VARIANCE_YEAR_LABEL_COLOR;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        for (const { year, start, end } of this.yearGroups) {
          const cx = (xAxis.getPixelForTick(start) + xAxis.getPixelForTick(end)) / 2;
          ctx.fillText(year, cx, chartArea.bottom + 36);
        }
        ctx.restore();
        for (let i = 1; i < this.yearGroups.length; i++) {
          const sepX =
            (xAxis.getPixelForTick(this.yearGroups[i].start - 1) + xAxis.getPixelForTick(this.yearGroups[i].start)) / 2;
          ctx.save();
          ctx.setLineDash([4, 4]);
          ctx.strokeStyle = this.labelColor + '55';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(sepX, chartArea.top - 30);
          ctx.lineTo(sepX, chartArea.bottom + 30);
          ctx.stroke();
          ctx.restore();
        }
      },
    },
  ];

  readonly gaugeChartPlugins: Plugin[] = [
    {
      id: 'gaugeCenterText',
      afterDatasetsDraw: (chart: Chart) => {
        const { ctx, chartArea } = chart;
        if (!chartArea || !this.summary || chart.data.datasets.length !== 1) return;
        const cx = (chartArea.left + chartArea.right) / 2;
        const cy = chartArea.bottom;
        ctx.save();
        ctx.font = 'bold 20px Inter, sans-serif';
        ctx.fillStyle = '#21b3ff';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        ctx.fillText(`${this.summary.netCsatScore}%`, cx, cy - 4);
        ctx.font = '400 10px Inter, sans-serif';
        ctx.fillStyle = this.labelColor;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';
        ctx.fillText('0%', chartArea.left, cy);
        ctx.textAlign = 'right';
        ctx.fillText('100%', chartArea.right, cy);
        ctx.restore();
      },
    },
  ];

  filtersForm = createTicketAnalysisFiltersForm();
  valuesMap: any = this.filtersForm.value;
  filtersConfigMap: Map<string, IFormControlConfig> = createTicketAnalysisFilterConfigMap();
  readonly filtersLayoutConfig = TICKET_ANALYSIS_FILTERS_LAYOUT;

  private readonly STORAGE_KEY = 'ticket_analysis_smileback';

  constructor(
    private readonly ticketAnalysisService: TicketAnalysisService,
    private readonly themeService: ThemeService,
    private readonly destroyRef: DestroyRef,
    private readonly viewPersistenceService: ViewPersistenceService,
  ) {}

  ngOnDestroy(): void {
    Chart.unregister(...this.csatChartPlugins, ...this.gaugeChartPlugins);
  }

  ngOnInit(): void {
    this.buildChartOptions();
    this.themeService.themeSubject$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.buildChartOptions();
      if (this.summary) {
        this.buildCharts(this.summary);
        this.buildGaugeChart(this.summary);
      }
    });
    this.restoreFilters();
    this.loadDropdowns();
    this.load();
  }

  updateValuesMap(valuesMap: any): void {
    this.valuesMap = valuesMap;
  }

  applyFilters(): void {
    this.saveFilters();
    this.load();
  }

  clearFilters(): void {
    this.filtersForm.reset({
      dateRange: { start: defaultDateFrom(), end: new Date() },
      site: null,
      board: null,
      contactName: null,
      status: null,
      source: null,
    });
    this.valuesMap = this.filtersForm.value;
    this.viewPersistenceService.clearFilters(this.STORAGE_KEY);
    this.load();
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.loadReviews();
  }

  ratingLabel(rating: SmilebackReview['rating']): string {
    const map: Record<string, string> = { positive: 'Positive', neutral: 'Neutral', negative: 'Negative' };
    return map[rating] ?? rating;
  }

  ratingClass(rating: SmilebackReview['rating']): string {
    const map: Record<string, string> = {
      positive: 'sb__rating--positive',
      neutral: 'sb__rating--neutral',
      negative: 'sb__rating--negative',
    };
    return map[rating] ?? '';
  }

  private load(): void {
    this.currentPage = 1;
    this.loading = true;
    this.error = '';
    this.ticketAnalysisService
      .getSmileback(this.buildFilters())
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.loading = false)),
      )
      .subscribe({
        next: (data) => {
          this.summary = data;
          this.buildCharts(data);
          this.buildGaugeChart(data);
        },
        error: () => (this.error = 'Failed to load SmileBack data.'),
      });
    this.loadReviews();
  }

  private loadReviews(): void {
    this.loadingReviews = true;
    this.ticketAnalysisService
      .getSmilebackReviews(this.buildFilters(), this.currentPage, this.pageSize)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.loadingReviews = false)),
      )
      .subscribe({
        next: (data) => (this.reviewsPage = data),
        error: () => (this.reviewsPage = null),
      });
  }

  private loadDropdowns(): void {
    this.ticketAnalysisService.getFilters().subscribe({
      next: (response) => {
        this.filtersConfigMap = applyFilterDropdowns(response, this.filtersConfigMap);
      },
    });
  }

  private buildFilters(): TicketAnalysisFilters {
    return extractBaseFilters(this.valuesMap);
  }

  private buildCharts(data: SmilebackSummary): void {
    const yearGroupMap = new Map<string, { start: number; end: number }>();
    const labels = data.byMonth.map(({ month }, index) => {
      const [year, m] = month.split('-');
      if (!yearGroupMap.has(year!)) {
        yearGroupMap.set(year!, { start: index, end: index });
      } else {
        yearGroupMap.get(year!)!.end = index;
      }
      return new Date(+year!, +m! - 1).toLocaleString('en-US', { month: 'short' }).toUpperCase();
    });
    this.yearGroups = Array.from(yearGroupMap.entries()).map(([year, range]) => ({ year, ...range }));

    const barDefaults = {
      barPercentage: BAR_PERCENTAGE,
      categoryPercentage: BAR_CATEGORY_PERCENTAGE,
      stack: 'reviews',
    };

    this.csatBarChartData = {
      labels,
      datasets: [
        {
          type: 'bar',
          label: 'Positive',
          data: data.byMonth.map((m) => m.positive),
          backgroundColor: SMILEBACK_POSITIVE_COLOR,
          ...barDefaults,
          order: 2,
        },
        {
          type: 'bar',
          label: 'Neutral',
          data: data.byMonth.map((m) => m.neutral),
          backgroundColor: SMILEBACK_NEUTRAL_COLOR,
          ...barDefaults,
          order: 2,
        },
        {
          type: 'bar',
          label: 'Negative',
          data: data.byMonth.map((m) => m.negative),
          backgroundColor: SMILEBACK_NEGATIVE_COLOR,
          ...barDefaults,
          order: 2,
        },
        {
          type: 'line',
          label: 'CSAT %',
          data: data.byMonth.map((m) => m.csatPct),
          borderColor: '#4d4d4f',
          backgroundColor: 'transparent',
          pointBackgroundColor: '#4d4d4f',
          pointRadius: 4,
          borderWidth: 2,
          tension: 0.3,
          yAxisID: 'y2',
          order: 1,
        },
      ],
    };
  }

  private buildGaugeChart(data: SmilebackSummary): void {
    const score = data.netCsatScore ?? 0;
    this.gaugeChartData = {
      datasets: [
        {
          data: [score, 100 - score],
          backgroundColor: ['#21b3ff', '#e0e0e0'],
          borderWidth: 0,
        },
      ],
    };
  }

  private buildChartOptions(): void {
    const isDark = this.themeService.isInDarkMode();
    this.labelColor = isDark ? '#F1F1F1' : '#555555';
    const gridColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';

    this.csatBarOptions = {
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { top: 44, bottom: 36 } },
      plugins: {
        legend: { display: false },
        tooltip: { enabled: true },
      },
      scales: {
        x: { stacked: true, ticks: { color: this.labelColor }, grid: { color: gridColor } },
        y: {
          stacked: true,
          ticks: { color: this.labelColor },
          grid: { color: gridColor },
          beginAtZero: true,
          suggestedMax: 100,
        },
        y2: {
          type: 'linear' as const,
          position: 'right' as const,
          min: 0,
          max: 100,
          display: false,
          grid: { display: false },
        },
      },
    };

    this.gaugeOptions = {
      responsive: true,
      maintainAspectRatio: false,
      circumference: 180,
      rotation: -90,
      cutout: '65%',
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false },
      },
    };
  }

  private saveFilters(): void {
    const dateRange = this.valuesMap?.dateRange as { start: Date; end: Date } | null;
    this.viewPersistenceService.saveFilters(this.STORAGE_KEY, {
      ...this.valuesMap,
      dateRange: dateRange
        ? { start: dateRange.start?.toISOString?.() ?? null, end: dateRange.end?.toISOString?.() ?? null }
        : null,
    });
  }

  private restoreFilters(): void {
    const saved = this.viewPersistenceService.loadFilters(this.STORAGE_KEY);
    if (!saved) return;
    const rawRange = saved['dateRange'] as { start: string; end: string } | null;
    const start = rawRange?.start ? new Date(rawRange.start) : defaultDateFrom();
    const end = rawRange?.end ? new Date(rawRange.end) : new Date();
    const asTree = (v: unknown) =>
      v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, any>) : null;
    const asBoard = (v: unknown) => (Array.isArray(v) && v.length > 0 ? v : null);
    const restored = {
      dateRange: { start, end },
      contactName: (saved['contactName'] as string | null) ?? null,
      board: asBoard(saved['board']),
      site: asTree(saved['site']),
      status: asTree(saved['status']),
      source: asTree(saved['source']),
    };
    this.filtersForm.patchValue(restored);
    this.valuesMap = restored;
  }
}
